import { useEffect, useMemo, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import Markdown from 'react-markdown'
import { toast } from 'sonner'

import { RichTextareaWithSubmit } from '@/components/composer/RichTextareaWithSubmit'
import { CreateEmailTemplateButton } from '@/components/CreateEmailTemplateButton'
import { EmailTemplateSelectItems } from '@/components/EmailTemplateSelectItems'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { isProduction } from '@/config/config'
import { parseError } from '@/lib/errors'
import { type EmailTemplate, useEmailTemplates } from '@/models/emailTemplate'
import { type InsuranceCarrier, useSendCarrierEmail } from '@/models/insuranceCarrier'
import { type SalesLead } from '@/models/salesLead'
import { useCurrentUser } from '@/models/user'
import { buildLeadEmailBody } from './leadEmailDefaults'

interface CarrierEmailComposerProps {
  lead: SalesLead
  carrier: InsuranceCarrier
  // Contact selected from the Email button / inbound activity; seeds the To picker.
  initialContactId: string
  // Reply mode: thread onto this activity's Gmail thread and hide To/Template/Subject.
  inReplyToActivityId?: string
  initialSubject?: string
  onSent: () => void
  onCancel: () => void
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || 'there'
}

function templateById(templates: EmailTemplate[], id: string | null): EmailTemplate | null {
  return templates.find((t) => t.id === id) ?? null
}

function initialSelectedId(templates: EmailTemplate[], defaultTemplateId: string | null): string | null {
  if (defaultTemplateId && templates.some((t) => t.id === defaultTemplateId)) return defaultTemplateId
  return templates[0]?.id ?? null
}

export function CarrierEmailComposer({
  lead,
  carrier,
  initialContactId,
  inReplyToActivityId,
  initialSubject,
  onSent,
  onCancel,
}: CarrierEmailComposerProps) {
  const sendEmail = useSendCarrierEmail()
  const { user } = useCurrentUser({ enabled: true })
  const { data: templates = [] } = useEmailTemplates()

  const isReply = !!inReplyToActivityId
  const emailContacts = carrier.contacts.filter((c) => c.email)
  const signature = user?.hubPreferences.emailSignature || ''
  const primaryContact = lead.contacts.find((c) => c.isPrimary) ?? lead.contacts.at(0) ?? null
  const driverName = useMemo(() => firstName(primaryContact?.name ?? ''), [primaryContact?.name])
  const defaultTemplateId = templates.find((t) => t.isDefault)?.id ?? null

  const rendered = (template: EmailTemplate | null) => ({
    subject: template ? template.subject : '',
    body: buildLeadEmailBody({ driverName, template: template?.body ?? '', signature }),
  })

  const [contactId, setContactId] = useState(initialContactId)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() =>
    initialSelectedId(templates, defaultTemplateId)
  )
  // Replies start from an empty body on the thread; composes seed from the template.
  const seed = isReply
    ? { subject: initialSubject ?? 'Re:', body: '' }
    : rendered(templateById(templates, selectedTemplateId))
  const [subject, setSubject] = useState(seed.subject)
  const [content, setContent] = useState(seed.body)
  const [editorKey, setEditorKey] = useState(0)
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null)

  const initialBody = isReply ? '' : rendered(templateById(templates, selectedTemplateId)).body
  const dirty = content !== initialBody

  // Re-prefill once templates load (they arrive async) — only in compose mode and
  // only while the draft is still pristine, so we never clobber the user's edits.
  useEffect(() => {
    if (isReply || dirty || templates.length === 0) return
    const id = initialSelectedId(templates, defaultTemplateId)
    setSelectedTemplateId(id)
    const r = rendered(templateById(templates, id))
    setSubject(r.subject)
    setContent(r.body)
    setEditorKey((k) => k + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length])

  if (emailContacts.length === 0) {
    return <p className="text-muted-foreground text-sm">This carrier has no contact with an email address.</p>
  }

  const contact = emailContacts.find((c) => c.id === contactId) ?? emailContacts[0]
  const canSend = content.trim().length > 0 && (isReply || subject.trim().length > 0) && !sendEmail.isPending

  function applyTemplate(id: string | null) {
    setSelectedTemplateId(id)
    const r = rendered(templateById(templates, id))
    setSubject(r.subject)
    setContent(r.body)
    setEditorKey((k) => k + 1)
  }

  function handleTemplateChange(id: string) {
    if (id === selectedTemplateId) return
    if (dirty) {
      setPendingTemplateId(id)
      return
    }
    applyTemplate(id)
  }

  function handleSend() {
    if (!canSend) return
    const htmlContent = renderToStaticMarkup(<Markdown>{content}</Markdown>)
    sendEmail.mutate(
      {
        leadId: lead.id,
        carrierId: carrier.id,
        contactId: contact.id,
        subject,
        content,
        htmlContent,
        inReplyToActivityId,
      },
      {
        onSuccess: () => {
          toast.success('Email sent')
          onSent()
        },
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const pendingTemplateName =
    pendingTemplateId !== null
      ? templates.find((t) => t.id === pendingTemplateId)?.displayName || 'Untitled'
      : null

  return (
    <div className="flex flex-col gap-3">
      {!isProduction && !isReply && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Dev mode: email will be sent to{' '}
          <span className="font-mono font-medium">{user?.email ?? 'your address'}</span>, not the carrier's
          address.
        </div>
      )}

      {!isReply && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">To:</span>
            <Select value={contact.id} onValueChange={setContactId} disabled={sendEmail.isPending}>
              <SelectTrigger className="w-fit max-w-full text-sm">
                <SelectValue placeholder="Select contact" />
              </SelectTrigger>
              <SelectContent>
                {emailContacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Template</label>
            {templates.length > 0 && selectedTemplateId !== null ? (
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateChange}
                disabled={sendEmail.isPending}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  <EmailTemplateSelectItems templates={templates} />
                </SelectContent>
              </Select>
            ) : (
              <CreateEmailTemplateButton />
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
        </>
      )}

      <div>
        {!isReply && <label className="mb-1 block text-sm font-medium">Message</label>}
        <RichTextareaWithSubmit
          editorKey={editorKey}
          value={content}
          onValueChange={setContent}
          placeholder="Write an email…"
          onSubmitMessage={handleSend}
          buttonProps={{ disabled: !canSend }}
          addonChildren={
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          }
        />
      </div>

      <Dialog open={pendingTemplateId !== null} onOpenChange={(open) => !open && setPendingTemplateId(null)}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Replace your current draft?</DialogTitle>
            <DialogDescription>
              Your edits will be replaced with template{' '}
              <span className="font-medium">&quot;{pendingTemplateName}&quot;</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingTemplateId(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingTemplateId !== null) applyTemplate(pendingTemplateId)
                setPendingTemplateId(null)
              }}
            >
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
